/**
 * STOCK ALPHA — the stock alpha platform's reads, one hook per panel.
 *
 * Every hook answers `{ loading, error, denied, … }`. `denied` is a role
 * refusal (most staff reads are research-analyst only): a state the screen
 * explains, never an error it shows. Rows carry symbols only (backend B1), so
 * lists name their rows through `useStockLabels`, one batched call per page.
 */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  STOCK_LABELS_BY_SYMBOLS,
  STOCK_ALPHA_RANKINGS, STOCK_ALPHA_MODEL_VERSIONS, STOCK_ALPHA_SECURITY, STOCK_ALPHA_VALUATION,
  STOCK_ALPHA_RECOMMENDATION, STOCK_ALPHA_RESEARCH, STOCK_ALPHA_PERSONAS, STOCK_ALPHA_BOOKS,
  STOCK_ALPHA_BOOK, STOCK_ALPHA_THESES, STOCK_ALPHA_THESIS, STOCK_ALPHA_PAPER_BOOKS,
  STOCK_ALPHA_PAPER_BOOK, STOCK_ALPHA_REPORTS, STOCK_ALPHA_JOB_HEALTH, STOCK_ALPHA_ALERTS,
  STOCK_ALPHA_MONITORS, STOCK_FACTOR_CATALOG, STOCK_FACTOR_FEASIBILITY, STOCK_ALPHA_EXPERIMENTS,
  STOCK_ALPHA_MODEL_DETAIL, STOCK_ALPHA_OPTIMISE, STOCK_COMPANY_REPORTS, STOCK_RAW_INPUTS, stockPricesDocument, optimiseManyDocument,
} from '../queries';
import { DEFAULT_STOCK_HORIZON, bookArgs, daysOld, isRoleRefusal } from '../../lib/stockAlpha';
import { SAMPLE_BENCHMARK, SAMPLE_RECIPES, markToMarket, recipeInput } from '../../lib/samplePortfolios';
import { EMPTY } from './_shared';

const READ = { fetchPolicy: 'cache-and-network', errorPolicy: 'all' };
const ONCE = { fetchPolicy: 'cache-first', errorPolicy: 'all' };

/** The three states every panel renders from. */
const stateOf = ({ loading, error }) => ({
  loading,
  error: error && !isRoleRefusal(error) ? error : null,
  denied: Boolean(error) && isRoleRefusal(error),
});

/**
 * Symbol → name and sector for a page of rows, in one call. Falls back to the
 * symbol so an unresolved name never blanks a row.
 */
export function useStockLabels(symbols) {
  const key = [...new Set((symbols ?? EMPTY).filter(Boolean))].sort().join('|');
  const { data } = useQuery(STOCK_LABELS_BY_SYMBOLS, {
    variables: { securitySymbols: key ? key.split('|') : EMPTY },
    skip: !key,
    ...ONCE,
  });
  return useMemo(() => {
    const by = new Map((data?.getStocksBySymbols ?? EMPTY).map((s) => [s.securitySymbol, s]));
    return {
      nameOf: (s) => by.get(s)?.name || s,
      sectorOf: (s) => by.get(s)?.sector || null,
    };
  }, [data]);
}

/* ── Ranks ────────────────────────────────────────────────── */

/**
 * One horizon's ranking with names and sectors joined on. The limit is high
 * on purpose: `totalScored` is the universe size, not the count passing
 * `minPercentile` (B5), so "shown N" can only count the rows that came back.
 */
export function useStockAlphaRanks({ horizon = DEFAULT_STOCK_HORIZON, asOfDate, minPercentile, limit = 600, skip = false } = {}) {
  const result = useQuery(STOCK_ALPHA_RANKINGS, {
    variables: {
      horizon,
      asOfDate: asOfDate || null,
      minPercentile: minPercentile ? Number(minPercentile) : null,
      limit,
    },
    skip: skip || !horizon,
    ...READ,
  });
  const page = result.data?.alphaRankings ?? null;
  const rows = page?.rows ?? EMPTY;
  const labels = useStockLabels(rows.map((r) => r.securitySymbol));
  return useMemo(() => ({
    ...stateOf(result),
    asOf: page?.asOfDate ?? null,
    totalScored: page?.totalScored ?? null,
    modelVersionId: page?.modelVersionId ?? null,
    refetch: result.refetch,
    rows: rows.map((r) => ({ ...r, name: labels.nameOf(r.securitySymbol), sector: labels.sectorOf(r.securitySymbol) })),
  }), [result, page, rows, labels]);
}

/**
 * Fitted versions for a horizon (all horizons when null), newest first, and
 * the one behind the ranking: the version id the ranking reports, else the
 * active one.
 */
export function useStockAlphaModels(horizon, modelVersionId) {
  const result = useQuery(STOCK_ALPHA_MODEL_VERSIONS, { variables: { horizon: horizon || null, limit: 20 }, ...READ });
  return useMemo(() => {
    const versions = result.data?.alphaModelVersions ?? EMPTY;
    const model = versions.find((v) => modelVersionId != null && v.id === modelVersionId)
      ?? versions.find((v) => v.isActive)
      ?? versions[0]
      ?? null;
    return { ...stateOf(result), versions, model };
  }, [result, modelVersionId]);
}

/* ── Why this stock ───────────────────────────────────────── */

export function useStockAlphaSecurity(symbol, asOfDate) {
  const result = useQuery(STOCK_ALPHA_SECURITY, {
    variables: { securitySymbol: symbol, asOfDate: asOfDate || null },
    skip: !symbol,
    ...READ,
  });
  const labels = useStockLabels(symbol ? [symbol] : EMPTY);
  return useMemo(() => {
    const view = result.data?.alphaSecurity ?? null;
    return {
      ...stateOf(result),
      asOf: view?.asOfDate ?? null,
      name: labels.nameOf(symbol),
      sector: labels.sectorOf(symbol),
      forecasts: view?.forecasts ?? EMPTY,
      factors: view?.factors ?? EMPTY,
      personas: view?.personas ?? EMPTY,
    };
  }, [result, labels, symbol]);
}

export function useStockAlphaValuation(symbol) {
  const result = useQuery(STOCK_ALPHA_VALUATION, { variables: { securitySymbol: symbol }, skip: !symbol, ...READ });
  return useMemo(() => ({
    ...stateOf(result),
    valuation: result.data?.alphaValuation ?? null,
    peers: result.data?.alphaPeers ?? null,
  }), [result]);
}

export function useStockAlphaRecommendation(symbol) {
  const result = useQuery(STOCK_ALPHA_RECOMMENDATION, { variables: { securitySymbol: symbol }, skip: !symbol, ...READ });
  return useMemo(() => ({ ...stateOf(result), recommendations: result.data?.alphaRecommendation ?? EMPTY }), [result]);
}

export function useStockAlphaResearch(symbol) {
  const result = useQuery(STOCK_ALPHA_RESEARCH, { variables: { securitySymbol: symbol }, skip: !symbol, ...READ });
  return useMemo(() => {
    const d = result.data;
    return {
      ...stateOf(result),
      panel: d?.alphaAnalystPanel ?? EMPTY,
      promises: d?.alphaPromises ?? EMPTY,
      news: d?.alphaNews ?? EMPTY,
      brief: d?.alphaCompanyBrief ?? null,
    };
  }, [result]);
}

/** The persona screens' shared, versioned disclaimer — rendered, never hard-coded. */
export function useStockPersonaDisclaimer() {
  const { data } = useQuery(STOCK_ALPHA_PERSONAS, ONCE);
  return data?.alphaPersonas?.find((p) => p.disclaimer)?.disclaimer ?? null;
}

/* ── Books ────────────────────────────────────────────────── */

export function useStockAlphaBooks() {
  const result = useQuery(STOCK_ALPHA_BOOKS, READ);
  return useMemo(() => ({ ...stateOf(result), books: result.data?.alphaModelPortfolios ?? EMPTY }), [result]);
}

export function useStockAlphaBook(portfolioId) {
  const result = useQuery(STOCK_ALPHA_BOOK, { variables: bookArgs(portfolioId), skip: !portfolioId, ...READ });
  const book = result.data?.alphaModelPortfolio ?? null;
  const labels = useStockLabels([
    ...(book?.holdings ?? EMPTY).map((h) => h.securitySymbol),
    ...(book?.trades ?? EMPTY).map((t) => t.securitySymbol),
  ]);
  return useMemo(() => ({ ...stateOf(result), book, labels }), [result, book, labels]);
}

/* ── Theses ───────────────────────────────────────────────── */

export function useStockAlphaTheses({ status, limit = 200, skip = false } = {}) {
  const result = useQuery(STOCK_ALPHA_THESES, { variables: { status: status || null, limit }, skip, ...READ });
  return useMemo(() => ({ ...stateOf(result), theses: result.data?.alphaTheses ?? EMPTY, refetch: result.refetch }), [result]);
}

export function useStockAlphaThesis(pick) {
  const result = useQuery(STOCK_ALPHA_THESIS, {
    variables: { securitySymbol: pick?.securitySymbol, horizon: pick?.horizon, version: pick?.version ?? null },
    skip: !pick?.securitySymbol || !pick?.horizon,
    ...READ,
  });
  return useMemo(() => ({ ...stateOf(result), thesis: result.data?.alphaThesis ?? null, refetch: result.refetch }), [result]);
}

/* ── Paper trading ────────────────────────────────────────── */

export function useStockAlphaPaperBooks(kind) {
  const result = useQuery(STOCK_ALPHA_PAPER_BOOKS, { variables: { kind: kind || null }, ...READ });
  return useMemo(() => ({ ...stateOf(result), books: result.data?.alphaPaperPortfolios ?? EMPTY, refetch: result.refetch }), [result]);
}

/** One paper book's NAV, fills and positions, with its simulated twin's NAV beside it. */
export function useStockAlphaPaperBook(portfolioId) {
  const result = useQuery(STOCK_ALPHA_PAPER_BOOK, {
    variables: { portfolioId, twinId: `${portfolioId}:sim` },
    skip: !portfolioId,
    ...READ,
  });
  const d = result.data;
  const labels = useStockLabels([
    ...(d?.alphaPaperPositions ?? EMPTY).map((p) => p.securitySymbol),
    ...(d?.alphaPaperFills ?? EMPTY).map((f) => f.securitySymbol),
  ]);
  return useMemo(() => ({
    ...stateOf(result),
    nav: d?.nav ?? EMPTY,
    twinNav: d?.twinNav ?? EMPTY,
    fills: d?.alphaPaperFills ?? EMPTY,
    positions: d?.alphaPaperPositions ?? EMPTY,
    labels,
  }), [result, d, labels]);
}

export function useStockAlphaReports(kind, limit = 20) {
  const result = useQuery(STOCK_ALPHA_REPORTS, { variables: { kind: kind || null, limit }, ...READ });
  return useMemo(() => ({ ...stateOf(result), reports: result.data?.alphaReports ?? EMPTY }), [result]);
}

/* ── Health ───────────────────────────────────────────────── */

/** `available: false` when this seat may not read job health — freshness then comes from the as-of date. */
/**
 * `skip` leaves the read unsent: on staging `alphaJobHealth` over ten
 * sessions runs past the gateway's 60 s and takes it down for minutes, so
 * the desk asks for it only where the job board is drawn and reads the
 * pipeline state from the ranking's age elsewhere (lib/stockAlpha
 * `pipelineState` already does that when health is unavailable).
 */
export function useStockAlphaJobHealth({ sessions = 10, skip = false } = {}) {
  const result = useQuery(STOCK_ALPHA_JOB_HEALTH, { variables: { sessions }, skip, ...READ });
  return useMemo(() => {
    const health = result.data?.alphaJobHealth ?? null;
    return {
      ...stateOf(result),
      available: Boolean(health) && !result.error,
      healthy: health?.healthy ?? null,
      since: health?.since ?? null,
      sessions: health?.sessions ?? sessions,
      jobs: health?.jobs ?? EMPTY,
      refetch: result.refetch,
    };
  }, [result, sessions]);
}

export function useStockAlphaAlerts({ status, limit = 200, skip = false } = {}) {
  const result = useQuery(STOCK_ALPHA_ALERTS, { variables: { status: status || null, limit }, skip, ...READ });
  return useMemo(() => ({ ...stateOf(result), alerts: result.data?.alphaAlerts ?? EMPTY, refetch: result.refetch }), [result]);
}

export function useStockAlphaMonitors() {
  const result = useQuery(STOCK_ALPHA_MONITORS, READ);
  return useMemo(() => ({ ...stateOf(result), monitors: result.data?.alphaMonitorLatest ?? EMPTY }), [result]);
}

/* ── Factors ──────────────────────────────────────────────── */

/**
 * The factor catalogue, once per session — the table on Factors and the
 * id → name / family lookup everywhere else. Both fall back, so a factor
 * added since the last deploy still renders.
 */
export function useStockFactorCatalog({ skip = false } = {}) {
  const result = useQuery(STOCK_FACTOR_CATALOG, { ...ONCE, skip });
  return useMemo(() => {
    const entries = result.data?.factorCatalog ?? EMPTY;
    const byId = new Map(entries.map((f) => [f.id, f]));
    return {
      ...stateOf(result),
      entries,
      byId,
      nameOf: (id) => byId.get(id)?.name || id,
      familyOf: (id) => byId.get(id)?.family || null,
    };
  }, [result]);
}

export function useStockFactorFeasibility() {
  const result = useQuery(STOCK_FACTOR_FEASIBILITY, ONCE);
  return useMemo(() => ({ ...stateOf(result), report: result.data?.factorFeasibility ?? null }), [result]);
}

/* ── Alpha Research ───────────────────────────────────────── */

export function useStockAlphaExperiments(horizon) {
  const result = useQuery(STOCK_ALPHA_EXPERIMENTS, { variables: { horizon: horizon || null, limit: 50 }, ...READ });
  return useMemo(() => ({ ...stateOf(result), experiments: result.data?.alphaExperiments ?? EMPTY }), [result]);
}

export function useStockAlphaModelDetail(modelVersionId) {
  const result = useQuery(STOCK_ALPHA_MODEL_DETAIL, { variables: { modelVersionId }, skip: modelVersionId == null, ...READ });
  return useMemo(() => ({
    ...stateOf(result),
    backtests: result.data?.alphaBacktests ?? EMPTY,
    card: result.data?.alphaModelCard ?? null,
  }), [result]);
}

/* ── Exposure optimiser ───────────────────────────────────── */

/**
 * A dry run of the concentrated builder. Keyed on its inputs, so editing a
 * constraint re-solves; nothing is written on the server.
 */
export function useStockAlphaOptimise(input, { skip = false } = {}) {
  const result = useQuery(STOCK_ALPHA_OPTIMISE, { variables: input, skip, fetchPolicy: 'cache-first', errorPolicy: 'all' });
  const run = result.data?.alphaOptimiseDryRun ?? null;
  const labels = useStockLabels([
    ...(run?.holdings ?? EMPTY).map((h) => h.securitySymbol),
    ...(run?.excluded ?? EMPTY).map((e) => e.securitySymbol),
  ]);
  return useMemo(() => ({ ...stateOf(result), run, labels }), [result, run, labels]);
}

/* ── Prices ───────────────────────────────────────────────── */

/**
 * symbol → daily price points, for a list of stocks, in one request.
 * `filter` is the backend's named range ('1m', '3m', '6m', '1y').
 */
export function useStockPrices(symbols, { filter = '1m', skip = false } = {}) {
  const key = [...new Set((symbols ?? EMPTY).filter(Boolean))].sort().join('|');
  const list = useMemo(() => (key ? key.split('|') : EMPTY), [key]);
  const variables = useMemo(() => {
    const v = { filter };
    list.forEach((s, i) => { v[`s${i}`] = s; });
    return v;
  }, [list, filter]);
  const result = useQuery(stockPricesDocument(Math.max(list.length, 1)), {
    variables,
    skip: skip || !list.length,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const history = {};
    list.forEach((s, i) => { history[s] = result.data?.[`p${i}`]?.stockHistory ?? EMPTY; });
    return { ...stateOf(result), history, ready: Boolean(result.data) };
  }, [result, list]);
}

/* ── Sample portfolios (My portfolios) ────────────────────── */

/** The named price range that reaches back to `asOf`. */
function filterReaching(asOf) {
  const age = daysOld(asOf) ?? 0;
  if (age <= 25) return '1m';
  if (age <= 85) return '3m';
  if (age <= 175) return '6m';
  return '1y';
}

/**
 * The sample shelf: every recipe run through the optimiser (one request),
 * every holding plus the benchmark priced (one request), each book held from
 * the ranking date to the latest close. Saved nowhere.
 */
export function useSamplePortfolios(recipes = SAMPLE_RECIPES) {
  const variables = useMemo(() => {
    const v = {};
    recipes.forEach((r, i) => {
      const x = recipeInput(r);
      Object.assign(v, {
        [`h${i}`]: x.horizon, [`t${i}`]: x.topN, [`n${i}`]: x.nNames,
        [`sc${i}`]: x.sectorCap, [`cap${i}`]: x.singleStockCap, [`min${i}`]: x.minPosition,
      });
    });
    return v;
  }, [recipes]);
  const runs = useQuery(optimiseManyDocument(recipes.length), { variables, fetchPolicy: 'cache-first', errorPolicy: 'all' });

  const built = useMemo(
    () => recipes.map((r, i) => ({ recipe: r, run: runs.data?.[`o${i}`] ?? null })),
    [recipes, runs.data],
  );
  const asOf = built.find((b) => b.run?.asOfDate)?.run.asOfDate ?? null;
  const symbols = useMemo(
    () => [SAMPLE_BENCHMARK.symbol, ...built.flatMap((b) => (b.run?.holdings ?? EMPTY).map((h) => h.securitySymbol))],
    [built],
  );
  const prices = useStockPrices(symbols, { filter: filterReaching(asOf), skip: !asOf });
  const labels = useStockLabels(symbols);

  return useMemo(() => ({
    ...stateOf(runs),
    pricesLoading: prices.loading,
    pricesError: prices.error,
    asOf,
    labels,
    portfolios: built.map(({ recipe, run }) => ({
      recipe,
      run,
      market: run && prices.ready && run.holdings.length
        ? markToMarket(run.holdings, prices.history, run.asOfDate)
        : null,
    })),
  }), [runs, prices, built, asOf, labels]);
}

/* ── Company research reports ─────────────────────────────── */

/**
 * A company's initiating-coverage reports, newest first. `pollInterval` is set
 * while a generation is running, so the new report appears without a reload.
 */
export function useCompanyReports(symbol, { pollInterval = 0 } = {}) {
  const result = useQuery(STOCK_COMPANY_REPORTS, {
    variables: { securitySymbol: symbol, limit: 10 },
    skip: !symbol,
    pollInterval,
    notifyOnNetworkStatusChange: false,
    ...READ,
  });
  return useMemo(() => ({
    ...stateOf(result),
    reports: result.data?.alphaCompanyReports ?? EMPTY,
    refetch: result.refetch,
  }), [result]);
}

/* ── Raw factor inputs ────────────────────────────────────── */

/**
 * The raw data a stock's factors are computed from: filed statements (newest
 * first), a year of daily prices, analyst estimates and results dates. Each
 * read fails on its own — a missing dataset leaves the others standing.
 */
export function useStockRawInputs(symbol) {
  const result = useQuery(STOCK_RAW_INPUTS, { variables: { securitySymbol: symbol }, skip: !symbol, ...ONCE });
  return useMemo(() => {
    const d = result.data ?? {};
    return {
      ...stateOf(result),
      statements: d.getStockFinancials ?? EMPTY,
      prices: d.getStockPriceHistory?.stockHistory ?? EMPTY,
      estimates: d.getStockEstimates ?? EMPTY,
      earnings: d.getStockEarnings ?? EMPTY,
    };
  }, [result]);
}
